import {
  cleanedPhoneNumber,
  convertKeysToCamelCase,
} from "@/services/api-data-mapping";
import {
  getFormattedDate,
  getFullName,
  getGroupByDate,
  getNameInitials,
  getParsedJson,
} from "@/services/generic.methods";
import { createSlice } from "@reduxjs/toolkit";
import moment from "moment";
import {
  Validate_Email,
  Validate_Number,
} from "@/modules/crm/leads/graphql-queries/general-queries";
import { GqlClient } from "@/services/graph-ql/configs";
import {
  avatarType,
  appointmentNotificationTypes,
  notificationTypes,
  textTypes,
  titleTypes,
} from "./types";
import storage from "@/services/storage";
import {
  faCalendar,
  faComments,
  faHourglassHalf,
  faMessage,
} from "@fortawesome/free-regular-svg-icons";
import {
  faBell,
  faListCheck,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import useFeatures from "@/hooks/useFeatures";
import FeatureService from "@/services/feature-service";
import store from "@/store";
import { selectFeatures } from "../features/featuresSlice";
import { FeatureKeys } from "@/services/dropdown-enums";

const GUID_REGEX =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * @name @getNotificationText
 * @description this will return text to be shown for the notification
 * @params notification object
 **/

export const getStaticExportName = (data = "", isReport = false) => {
  const profile = JSON.parse(storage.getItem("Profile"));
  const fileName = typeof data === "object" ? data?.file_name : data;
  if (fileName) {
    let text = fileName.split(".");
    let ext = text[text.length - 1];
    if (isReport) {
      let nameWithoutExt = fileName.substring(0, fileName.lastIndexOf("."));
      let cleanedName = nameWithoutExt.replace(
        /-[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i,
        ""
      );
      return `${cleanedName}.${ext}`;
    }
    return `${profile?.CompanyName.trim()} Leads.${ext}`;
  }
};
const getNotificationText = (notification) => {
  switch (notification.type) {
    case notificationTypes.leads.assinged:
      const isCurrentUser =
        +notification.loggedInUserId === +notification?.assignee?.id;
      const selfAssign =
        +notification?.from?.id === +notification?.assignee?.id;
      return textTypes.leads[notification.type](
        notification?.assignee?.fullName,
        notification?.from?.fullName,
        isCurrentUser,
        selfAssign
      );
    case notificationTypes.leads.received:
    case notificationTypes.leads.optin:
      return notification.description;
    case notificationTypes.leads.statusChanged:
      return textTypes.leads[notification.type](notification?.from?.name);
    case notificationTypes.leads.sms:
      return textTypes.leads[notification.type](notification?.from?.name);
    case notificationTypes.leads.leadFailed:
      return textTypes.leads[notification.type](notification?.assignee);
    case notificationTypes.leads.leadProcessing:
      return textTypes.leads[notification.type](notification?.assignee);
    case notificationTypes.leads.export:
      return textTypes.leads[notification.type](
        getStaticExportName(notification.data)
      );
    case notificationTypes.leads.reportExport:
      return textTypes.leads[notification.type](
        getStaticExportName(notification.data, true)
      );
    case notificationTypes.leads.bulkAssigned:
      const selfAssigned =
        +notification?.from?.id === +notification?.assignee?.id;
      return textTypes.leads[notification.type](
        notification.data,
        selfAssigned
      );
    case notificationTypes.leads.emailReceived:
      return notification.description;
    default:
    // return textTypes.leads[notification.type]();
  }
};

const getNotificationMessage = (notification) => {
  switch (notification.type) {
    case notificationTypes.leads.added:
      if (notification.data?.count && notification.data?.leadImportedBy) {
        const selfAssign =
          +notification.loggedInUserId ===
          +notification?.data?.leadImportedBy?.id;
        return {
          text: textTypes.leads[notification.type](
            notification?.data,
            selfAssign
          ),
        };
      }
      return {
        text: notification.description,
        sender: {
          name: notification.from?.fullName,
        },
      };
    case notificationTypes.leads.sms:
      return {
        text: notification?.data?.text,
        sender: {
          name: notification.from?.fullName,
        },
      };
    case notificationTypes.leads.emailReceived:
      let { data, from } = notification;
      let info = getParsedJson(data?.text);
      let subject = info?.subject?.replace("Re:", "");
      let isNameAvailable = from?.firstName || from?.lastName;
      return {
        text: subject || "(no subject)",
        sender: {
          name: isNameAvailable ? from?.fullName : from?.email,
        },
      };
    case notificationTypes.leads.assinged:
    case notificationTypes.leads.bulkAssigned:
    case notificationTypes.leads.received:
    case notificationTypes.leads.optin:
      return null;
    case notificationTypes.leads.emailOpened:
    case notificationTypes.leads.emailClicked:
      let leadOpenedByName = notification.from?.firstName
        ? `${notification.from?.firstName} ${notification.from?.lastName || ""}`
        : notification.from?.email || "Recipient";
      let emailType = "Opened";
      if (notification.type == notificationTypes.leads.emailClicked) {
        emailType = "Clicked";
      }
      return {
        text: `Email ${emailType} by ${leadOpenedByName}`,
        sender: {
          name: notification.from?.fullName,
        },
      };
    case notificationTypes.task.reminderTrigged: {
      return {
        text: `${notification?.data?.title} is due on ${getFormattedDate(notification?.data?.dueDate, "DD/MM/YYYY hh:mm A")}. Please complete it on time.`,
      };
    }
    default:
      return {
        text: notification?.description,
        sender: {
          name: notification.from?.fullName,
        },
      };
  }
};
const getNotificationLabels = (notification) => {
  switch (notification.type) {
    case notificationTypes.leads.optin:
      return ["SMS Opt-in"];

    default:
      return [];
  }
};

const getNotificationAvatar = (notification, icon) => {
  return notification?.from?.avatar
    ? avatarType.avatar
    : icon
      ? avatarType.icon
      : avatarType.initials;
};

const getTitle = (notification) => {
  if (notification?.type === notificationTypes.leads.emailReceived) {
    return titleTypes.leads[notification.type];
  }
  return notification?.title ?? titleTypes.leads[notification.type];
};

const getLeadEmailOrNumber = async (notificationData = {}) => {
  const leadInfo = notificationData || {};
  const email = leadInfo.email;

  if (email) {
    try {
      const response = await GqlClient.query({
        query: Validate_Email,
        variables: { email },
        fetchPolicy: "no-cache",
      });
      return response?.data?.leadsGrid?.items?.[0]?.id ?? null;
    } catch (error) {
      console.error("Error resolving lead by email:", error);
      return null;
    }
  }

  // const phone = leadInfo.phone;
  // const countryCode = leadInfo.countryCode;

  // if (phone) {
  //   try {
  //     const response = await GqlClient.query({
  //       query: Validate_Number,
  //       variables: {
  //         phone: cleanedPhoneNumber(phone),
  //         // countryCode,
  //       },
  //       fetchPolicy: "no-cache",
  //     });
  //     return response?.data?.leadsGrid?.items?.[0]?.id ?? null;
  //   } catch (error) {
  //     console.error("Error resolving lead by phone:", error);
  //     return null;
  //   }
  // }

  return null;
};

const mapNotificationItem = async (notification) => {
  const features = selectFeatures(store.getState());
  const hasCRM = FeatureService.hasFeature(features, FeatureKeys.CRM);
  const parsedData = getParsedJson(notification?.data);
  notification.data = convertKeysToCamelCase(parsedData);
  notification.from =
    notification?.data?.leadAssignedBy ?? notification?.data?.leadInfo;
  notification.assignee = notification?.data?.assigneeUserInfo;
  notification.leadId =
    notification?.data?.lead_id ||
    notification?.data?.leadId ||
    notification.data?.leadInfo?.id;
  if (
    hasCRM &&
    notification.leadId &&
    GUID_REGEX.test(String(notification.leadId))
  ) {
    const resolvedLeadId = await getLeadEmailOrNumber(notification?.data);
    if (resolvedLeadId) {
      notification.leadId = resolvedLeadId;
    }
  }

  if (notification?.from) {
    notification.from.fullName = notification?.data?.leadAssignedBy
      ? (notification?.from?.firstName ?? "Anonymous")
      : getFullName(notification?.from);
  }
  if (notification?.assignee) {
    notification.assignee.fullName = getFullName(notification?.assignee);
  }

  let icon = null;
  if (
    notification?.type === notificationTypes?.leads?.assinged ||
    notification?.type === notificationTypes?.leads?.bulkAssigned
  ) {
    icon = null;
  } else if (
    notification?.type === notificationTypes.leads.sms ||
    notification?.type === notificationTypes.leads.smsFailed ||
    notification?.type === notificationTypes.leads.received
  ) {
    icon = faMessage;
  }
  if (
    notification?.type === notificationTypes.task.taskAssigned ||
    notification?.type === notificationTypes.task.taskCompleted ||
    notification?.type === notificationTypes.task.taskStatus
  ) {
    icon = faListCheck;
  }
  if (notification?.type === notificationTypes.task.taskComment) {
    icon = faComments;
  }

  let variant = "default";
  if (appointmentNotificationTypes.reminder.includes(notification?.type)) {
    icon = faBell;
    variant = "upcoming";
    notification.leadId = notification?.data;
  } else if (
    appointmentNotificationTypes.booked.includes(notification?.type) ||
    appointmentNotificationTypes.cancelled.includes(notification?.type) ||
    appointmentNotificationTypes.rescheduled.includes(notification?.type)
  ) {
    icon = faCalendar;
    variant = "appointment";
  } else if (notification?.type === notificationTypes.task.reminderTrigged) {
    icon = faHourglassHalf;
    notification.leadId = notification?.data;
  }

  if (notification?.type === notificationTypes.leads.callReceived) {
    icon = faPhone;
  }

  return {
    ...notification,
    time: moment.utc(notification?.createdOn).local().fromNow(),
    avatar: notification?.from?.avatar,
    icon,
    variant,
    initials: notification?.from?.fullName
      ? getNameInitials(notification?.from?.fullName)
      : "",
    avatarType: getNotificationAvatar(notification, icon),
    title: getTitle(notification),
    text: getNotificationText(notification),
    message: getNotificationMessage(notification),
    labels: getNotificationLabels(notification),
    isNotificationRead: notification.isRead,
    dateGroup: getGroupByDate(notification.createdOn),
  };
};

const mapNotificationsAsync = async (notifications) => {
  const notificationsData = await Promise.all(
    (notifications ?? []).map((notification) =>
      mapNotificationItem(notification)
    )
  );

  return notificationsData.sort((a, b) =>
    moment.utc(b.createdOn).diff(moment.utc(a.createdOn))
  );
};
/**
 * @name @createSlice
 * @description create redux store slice for notifications
 **/
export const notificationsSlice = createSlice({
  name: "notifications",
  initialState: {
    notifications: [],
    count: 0,
    unreadCount: 0,
    pageInfo: { skip: 0, take: 10, hasNextPage: false },
    isMuted: false,
  },

  reducers: {
    /**
     * @name @setNotificationsToState
     * @description setNotificationsToState to set notification to state
     **/
    appendMappedNotifications: (state, action) => {
      state.notifications.push(...action.payload);
    },
    prependMappedNotification: (state, action) => {
      const notification = action.payload;
      const notificationId = notification?.id;
      const isDuplicate = state.notifications.some(
        (n) => n.id === notificationId
      );
      if (isDuplicate) {
        console.log(`Notification with ID ${notificationId} already exists.`);
        return;
      }
      state.notifications.unshift(notification);
    },
    updateNotificationToState: (state, action) => {
      let notification = action?.payload;
      state.notifications = state.notifications.map((notif) => {
        if (notif.id == notification.id) {
          return notification;
        }
        return notif;
      });
    },
    setMarkAsRead: (state, action) => {
      // map data as per the component requirement
      // this could be changed with real data
      let data = [...state.notifications];
      const notificationId = action?.payload?.Id ?? action?.payload?.id;
      data = data.map((item) => {
        if (item.id === notificationId) {
          return { ...item, isNotificationRead: 1 };
        }
        return item;
      });

      state.notifications = [...data];
    },
    setMarkAllAsRead: (state) => {
      state.notifications = state.notifications.map((item) => ({
        ...item,
        isNotificationRead: 1,
      }));
    },
    setPageInfo: (state, action) => {
      const { recordsCount, ...rest } = action.payload;
      const take = rest.take ?? state.pageInfo.take ?? 10;
      state.pageInfo = {
        ...state.pageInfo,
        ...rest,
        take,
        skip: (state.pageInfo.skip || 0) + (recordsCount || 0),
        hasNextPage: (recordsCount || 0) >= take,
      };
    },
    clearNotifications: (state, action) => {
      state.notifications = [];
      state.count = 0;
      state.unreadCount = 0;
      state.pageInfo = { skip: 0, take: 10, hasNextPage: false };
    },
    toggleMute: (state) => {
      state.isMuted = !state.isMuted;
    },
  },
});

export const {
  appendMappedNotifications,
  prependMappedNotification,
  updateNotificationToState,
  setMarkAsRead,
  setMarkAllAsRead,
  setPageInfo,
  clearNotifications,
  toggleMute,
} = notificationsSlice.actions;

export const setNotificationsToState = (payload) => async (dispatch) => {
  const mapped = await mapNotificationsAsync(payload);
  dispatch(appendMappedNotifications(mapped));
};

export const setNotificationToState = (payload) => async (dispatch) => {
  const notificationId = payload?.Id ?? payload?.id;
  const notification = {
    ...payload,
    id: notificationId,
  };
  const [mappedNotification] = await mapNotificationsAsync([notification]);
  dispatch(
    prependMappedNotification(convertKeysToCamelCase(mappedNotification))
  );
};

/**
 * These function called thunk and allows to perform async logic
 * It can be dispatched like a regular action: `dispatch(data)`
 **/
export const getNotificationsAsync = (payload) => async (dispatch) => {
  const mapped = await mapNotificationsAsync(payload);
  dispatch(appendMappedNotifications(mapped));
  return mapped;
};

/* called a selector and allows to select values from the state */
export const selectNotifications = (state) => {
  return state.notifications;
};
export const selectNotificationsCount = (state) => {
  return state.notifications?.notifications?.length;
};
export const getPageInfo = (state) => {
  return state.notifications?.pageInfo;
};
export const selectNotificationsUnreadCount = (state) => {
  return state.notifications?.notifications?.filter(
    (notification) => !notification?.isNotificationRead
  )?.length;
};
export const selectIsMuted = (state) => {
  return state.notifications?.isMuted;
};

export default notificationsSlice.reducer;
